<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-auto">
    <RoleModal
      :isOpen="isModalOpen"
      :editRole="selectedRole"
      @close="closeModal"
      @save="handleSaveRole"
      @update="handleUpdateRole"
    />

    <!-- Table -->
    <div class="w-full overflow-x-auto overflow-y-auto max-h-96">
      <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
        <thead>
          <tr>
            <th>No.</th>
            <th @click="sortBy('name')" class="sort-col">
              Name <span v-if="sortKey === 'name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('remark')" class="sort-col">
              Remark <span v-if="sortKey === 'remark'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('created_at')" class="sort-col">
              Created At <span v-if="sortKey === 'created_at'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(role, index) in sortedRoles" :key="role.id" class="hover:bg-slate-100/50 transition-colors border-b">
            <td>{{ index + 1 }}</td>
            <td>{{ role.name }}</td>
            <td class="max-w-[320px] truncate">{{ role.remark || '-' }}</td>
            <td>{{ formatDate(role.created_at) }}</td>
            <td>
              <div class="flex items-center gap-2 whitespace-nowrap">
                <button
                  type="button"
                  @click="openEditModal(role)"
                  class="editbtn text-emerald-600"
                  :class="canEditRole ? 'hover:underline' : 'opacity-50 cursor-not-allowed action-disabled-marker'"
                  :disabled="!canEditRole"
                >
                  Edit
                </button>

                <label
                  class="toggle-switch"
                  :class="[
                    role.__togglingStatus ? 'opacity-50 pointer-events-none' : '',
                    !canEditRole ? 'opacity-50 cursor-not-allowed action-disabled-marker' : ''
                  ]"
                  title="Toggle status"
                >
                  <input
                    type="checkbox"
                    :checked="(role.status ?? 1) === 1"
                    :disabled="!canEditRole"
                    @change="toggleRoleStatus(role)"
                  >
                  <span class="slider"></span>
                </label>
              </div>
            </td>
          </tr>
          <tr v-if="loading">
  <td colspan="9" class="text-center py-10">
    <div class="flex items-center justify-center">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </td>
</tr>
          <tr v-else-if="roles.length === 0">
            <td colspan="5" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
              No roles found for this company.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import RoleModal from './RoleModal.vue'
import { apiFetch, WS_BASE_URL } from '../../utils/api'
import store from '../../store'

const roles = ref([])
const loading = ref(false)
const isModalOpen = ref(false)
const selectedRole = ref(null)
const sortKey = ref('name')
const sortOrder = ref('asc')

let roleSyncSocket = null
let roleSyncReconnectTimer = null

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)
const userId = computed(() => currentUser.value?.id || null)
const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const rolePrivilege = computed(() => userPrivileges.value?.Roles || userPrivileges.value?.roles || null)
const canAddRole = computed(() => hasAdminPrivilege.value || !!rolePrivilege.value?.Add)
const canEditRole = computed(() => hasAdminPrivilege.value || !!rolePrivilege.value?.Edit)

const sortedRoles = computed(() => {
  const result = [...roles.value]
  result.sort((a, b) => {
    const modifier = sortOrder.value === 'asc' ? 1 : -1
    const valA = (a[sortKey.value] ?? '').toString().toLowerCase()
    const valB = (b[sortKey.value] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })
  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

function formatDate(value) {
  if (!value) return '-'
  return new Date(value).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  })
}

async function loadRoles() {
  if (!companyId.value) return
  
  loading.value = true
  try {
    let endpoint = `/roles/company/${companyId.value}`
    if (branchId.value) {
      endpoint = `/roles/branch/${branchId.value}`
    }
    
    const response = await apiFetch(endpoint, { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load roles')
    }
    roles.value = await response.json()
  } catch (error) {
    console.error('Error loading roles:', error)
  } finally {
    loading.value = false
  }
}

async function handleSaveRole(payload) {
  try {
    await createRole(payload)
    closeModal()
    loadRoles()
  } catch (error) {
    console.error('Error saving role:', error)
    alert(error.message)
  }
}

async function handleUpdateRole(payload) {
  try {
    await updateRole(payload)
    closeModal()
    loadRoles()
  } catch (error) {
    console.error('Error updating role:', error)
    alert(error.message)
  }
}

async function createRole(payload) {
  const response = await apiFetch('/roles', {
    method: 'POST',
    body: JSON.stringify({
      companyId: branchId.value ? null : companyId.value,
      branchId: branchId.value || null,
      name: payload.name,
      remark: payload.remark || null,
      created_by: userId.value,
    }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Failed to create role')
  }
}

async function updateRole(payload) {
  const response = await apiFetch(`/roles/${payload.id}`, {
    method: 'PUT',
    body: JSON.stringify({
      name: payload.name,
      remark: payload.remark || null,
      updated_by: userId.value,
    }),
  })

  if (!response.ok) {
    const data = await response.json().catch(() => ({}))
    throw new Error(data.message || 'Failed to update role')
  }
}

async function toggleRoleStatus(role) {
  if (!canEditRole.value) return
  if (!role?.id || role.__togglingStatus) return
  role.__togglingStatus = true
  const current = (role.status ?? 1) === 1 ? 1 : 0
  const next = current === 1 ? 0 : 1
  try {
    const res = await apiFetch(`/roles/${role.id}/toggle-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next })
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to toggle role status')
    }
    role.status = next
  } catch (e) {
    alert(e.message || 'Failed to toggle role status')
  } finally {
    role.__togglingStatus = false
  }
}

function openAddModal() {
  if (!canAddRole.value) return
  selectedRole.value = null
  isModalOpen.value = true
}

function openEditModal(role) {
  if (!canEditRole.value) return
  selectedRole.value = role
  isModalOpen.value = true
}

function closeModal() {
  isModalOpen.value = false
  selectedRole.value = null
}

const handleOpenModal = () => openAddModal()

function disconnectRoleSyncRealtime() {
  if (roleSyncReconnectTimer) {
    clearTimeout(roleSyncReconnectTimer)
    roleSyncReconnectTimer = null
  }
  if (roleSyncSocket) {
    try {
      roleSyncSocket.close()
    } catch (_) {
      // ignore close failures
    }
    roleSyncSocket = null
  }
}

function connectRoleSyncRealtime() {
  disconnectRoleSyncRealtime()

  const user = currentUser.value
  if (!user || typeof WebSocket === 'undefined') return

  const scopedCompanyId = user.companyId || user.company_id || user.company?.id
  if (!scopedCompanyId) return

  const scopedBranchId = user.branchId || user.branch_id || user.branch?.id || null
  const socket = new WebSocket(WS_BASE_URL)
  roleSyncSocket = socket

  socket.addEventListener('open', () => {
    socket.send(JSON.stringify({
      type: 'frontend.subscribe',
      company_id: scopedCompanyId,
      branch_id: scopedBranchId,
    }))
  })

  socket.addEventListener('message', (event) => {
    try {
      const message = JSON.parse(event.data)
      if (message.event?.startsWith('role.')) {
        loadRoles()
      }
    } catch (_) {
      // ignore malformed realtime payloads
    }
  })

  socket.addEventListener('close', () => {
    roleSyncSocket = null
    roleSyncReconnectTimer = setTimeout(() => {
      connectRoleSyncRealtime()
    }, 3000)
  })
}

onMounted(() => {
  loadRoles()
  connectRoleSyncRealtime()
  window.addEventListener('open-role-modal', handleOpenModal)
})

onUnmounted(() => {
  disconnectRoleSyncRealtime()
  window.removeEventListener('open-role-modal', handleOpenModal)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: #1e293b;
}

th,
td {
  padding: 5px 6px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}

.editbtn {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  text-decoration: underline;
  font-size: 0.85rem;
  padding: 0;
  display: flex;
  align-items: center;
}

/* Toggle Switch (same style as departmentPage.vue / usersPage.vue) */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}

.action-disabled-marker {
  position: relative;
}

.action-disabled-marker:hover::after {
  content: '⊘';
  position: absolute;
  top: -8px;
  right: -10px;
  font-size: 12px;
  font-weight: 700;
  color: #dc2626;
  line-height: 1;
  pointer-events: none;
}
</style>
